import { Component, computed, EventEmitter, inject, Input, input, Output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PortraitService } from '../../../services/portraits/portrait.service';
import { PlayerContextMenu } from "../../menus/player-context-menu/player-context-menu";
import { MessengerService } from '../../../services/messenger/messenger.service';
import { ChatService } from '../../../services/chat/chat.service';
import { IdentityService } from '../../../services/identity/identity.service';
import { ToasterService } from '../../../services/toaster/toaster.service';

@Component({
  selector: 'app-player-panel',
  imports: [PlayerContextMenu],
  templateUrl: './player-panel.html',
  styleUrl: './player-panel.scss',
})
export class PlayerPanel {
  private router = inject(Router);
  private portraitService = inject(PortraitService);
  private messenger = inject(MessengerService);
  private chatService = inject(ChatService);
  private identityService = inject(IdentityService);
  private toaster = inject(ToasterService);

  portraits = this.portraitService.portraits;

  player = input.required<any>();
  showModerator = input<boolean>(false);

  @Input()
  canDelete: boolean = false;

  confirmDeleteActive = signal(false);
  isSwipedOpen = signal(false);
  private startX = 0;
  private startY = 0;
  private isPointerDown = false;

  isModerator = computed(() => {
    if (!this.showModerator()) return false;

    const arena = this.chatService.arena();
    if (!arena || arena.parentGuid == null) return false;

    const p = this.player();
    if (!p) return false;

    const playerId = p.id || p.guid || p.userId;
    const isOwner =
      (playerId && arena.ownerId === playerId) ||
      (p.userName && arena.ownerId === p.userName);

    if (isOwner) return true;

    if (p.role === 'admin' || p.role === 'moderator') return true;

    return false;
  });

  @Output()
  remove = new EventEmitter<any>();

  isFriend = computed(() => {
    return this.messenger.friends().some(f => f.userName === this.player().userName);
  });

  isLobbyOwner = computed(() => {
    const arena = this.chatService.arena();
    if (!arena) return false;

    const claims = this.identityService.getClaims();
    if (!claims) return false;

    // Check against standard JWT claims for User ID
    const userId = claims.nameid || claims.sub || claims.name;
    return arena.ownerId === userId;
  });

  isParentLobbyOwner = computed(() => {
    const arena = this.chatService.arena();
    if (!arena) return false;

    const p = this.player();
    if (!p) return false;

    const playerId = p.id || p.guid || p.userId;
    const parentOwnerId = arena.parentGuid == null ? arena.ownerId : arena.parentOwnerId;

    if (!parentOwnerId) return false;

    return (playerId && playerId === parentOwnerId) || (p.userName && p.userName === parentOwnerId);
  });

  activeIconUrl = computed(() => {
    const p = this.player();
    if (!p) return null;

    if (p.userIcons && Array.isArray(p.userIcons)) {
      const activeIcon = p.userIcons.find((icon: any) => icon.active);
      if (activeIcon && activeIcon.fileName) {
        return 'https://static.h5arena.com/images/icons/' + activeIcon.fileName;
      }
    }

    if (p.activeIcon) {
      const fileName = typeof p.activeIcon === 'object' ? p.activeIcon.fileName : p.activeIcon;
      if (fileName && typeof fileName === 'string') {
        return 'https://static.h5arena.com/images/icons/' + fileName;
      }
    }

    return null;
  });

  activeIconDescription = computed(() => {
    const p = this.player();
    if (!p) return '';

    if (p.userIcons && Array.isArray(p.userIcons)) {
      const activeIcon = p.userIcons.find((icon: any) => icon.active);
      if (activeIcon) {
        return activeIcon.description || '';
      }
    }

    if (p.activeIcon && typeof p.activeIcon === 'object') {
      return p.activeIcon.description || '';
    }
    return '';
  });

  getFriendPortrait = (id: any) => computed(() => {
    if (this.portraits().length > 0) {
      let portrait = this.portraits().filter(e => { return e.id == id; })[0];
      if (portrait) {
        return portrait.fileName;
      }
      else
        return "bagfacepak/2.jpg";
    }
    else
      return "bagfacepak/2.jpg";
  });

  getStatusColor(status: any) {
    switch (status) {
      case 1: return 'text-success'; // online
      case 2: return 'text-danger'; // afk
      case 3: return 'text-warning'; // busy
      case 4: return 'text-primary'; // in-game
      case 5: return 'text-warning'; // ready
      default: return 'text-secondary';
    }
  }

  getStatusText(status: any) {
    switch (status) {
      case 1: return 'online'; // online
      case 2: return 'away'; // afk
      case 3: return 'busy'; // busy
      case 4: return 'ingame'; // in-game
      case 5: return 'ready'; // ready
      default: return 'offline';
    }
  }

  handleContextMenuAction(event: { action: string, player: any }) {
    console.log("Context menu action: ", event.action, " for player ", event.player.userName);
    const userId = event.player.id || event.player.userId;

    switch (event.action) {
      case 'AddFriend':
        this.messenger.sendFriendRequest({
          toUserid: userId,
          fromUserId: "",
          comment: "friend request from rival",
          sent: new Date().toISOString()
        });
        break;
      case 'SendMessage':
        let conversation = this.messenger.conversations().find(c => c.friendUserId === userId);
        if (!conversation) {
          conversation = {
            friendUserId: userId,
            friend: event.player,
            messages: []
          };
          this.messenger.conversations.update(convs => [...convs, conversation]);
        }
        this.messenger.activeConversationId.set(userId);
        
        const bootstrap = (window as any).bootstrap;
        if (bootstrap && bootstrap.Offcanvas) {
          const el = document.getElementById('pagerOffcanvas');
          if (el) {
            const bsOffcanvas = bootstrap.Offcanvas.getInstance(el) || new bootstrap.Offcanvas(el);
            bsOffcanvas.show();
          }
        }
        break;
      case 'SendInvite': {
        const currentArena = this.chatService.arena();
        if (!currentArena) {
          this.toaster.warning("You must be in an arena to send an invite.");
          break;
        }

        const destination = currentArena.guid;
        const password = this.chatService.getPasswordForArena(destination) ?? "";

        const messageText = `Join me: ${currentArena.title}`;
        const directMessage = {
          toUserId: userId,
          text: messageText,
          color: "#FFFFFF",
          isMe: true
        };

        this.messenger.sendInvite(directMessage, destination, password);
        this.toaster.success(`Invite sent to ${event.player.userName}`);
        break;
      }
      case 'PassModerator': {
        const currentArena = this.chatService.arena();
        if (!currentArena) break;

        const targetId = userId || event.player.guid;
        const updatedArena = {
          ...currentArena,
          ownerId: targetId
        };

        this.chatService.update(updatedArena);
        break;
      }
      case 'Kick':
        if (userId) {
          this.chatService.kick(userId);
        }
        break;
      case 'Remove':
        this.messenger.removeFriend(event.player);
        this.messenger.friends.update(friends => friends.filter(f => (f.id || f.userId) !== userId));
        this.remove.emit(event.player);
        break;
    }
  }

  onDeleteClick(event: Event) {
    event.stopPropagation();
    if (this.confirmDeleteActive()) {
      const friend = this.player();
      const friendId = friend.id || friend.userId;

      this.messenger.removeFriend(friend);
      this.messenger.friends.update(friends => friends.filter(f => (f.id || f.userId) !== friendId));
      this.remove.emit(friend);

      this.confirmDeleteActive.set(false);
      this.isSwipedOpen.set(false);
    } else {
      this.confirmDeleteActive.set(true);
    }
  }

  onPointerDown(event: PointerEvent) {
    if (event.button !== 0) return;

    // Ignore swipe gesture if clicking/tapping the remove button directly
    const target = event.target as HTMLElement;
    if (target.closest('.remove-btn')) {
      return;
    }

    this.startX = event.clientX;
    this.startY = event.clientY;
    this.isPointerDown = true;
    const el = event.currentTarget as HTMLElement;
    try {
      el.setPointerCapture(event.pointerId);
    } catch (e) {}
  }

  onPointerMove(event: PointerEvent) {
    if (!this.isPointerDown) return;
    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
      this.isPointerDown = false;
      return;
    }

    if (deltaX < -50) {
      this.isSwipedOpen.set(true);
      this.isPointerDown = false;
    } else if (deltaX > 50) {
      this.isSwipedOpen.set(false);
      this.confirmDeleteActive.set(false);
      this.isPointerDown = false;
    }
  }

  onPointerUp(event: PointerEvent) {
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    const el = event.currentTarget as HTMLElement;
    try {
      el.releasePointerCapture(event.pointerId);
    } catch (e) {}
  }

  closeSwipe() {
    this.isSwipedOpen.set(false);
    this.confirmDeleteActive.set(false);
    this.isPointerDown = false;
  }

  onDblClick() {
    this.chatService.selectedUserForModal.set(this.player());
    const modalEl = document.getElementById('userProfileModal');
    if (modalEl) {
      const modal = new (window as any).bootstrap.Modal(modalEl);
      modal.show();
    }
  }
}
